import classNames from 'classnames';
import { useRouter } from 'next/router';
import type React from 'react';
import type { Contentful } from '../../contentful';
import { openLinkCurrentTab } from '../../contentful/utils';
import RightArrow from '../../public/cmsAssets/assets/img/RightArrow';
import { type Event, trackEvents } from '../../utils/analytics';
import useStyles from './OldButton.styles';

/**
 * @deprecated use /BaseLibraryButton/Button.tsx instead
 */
export enum ButtonVariant {
  PrimaryBluePill = 'PrimaryBluePill',
  TransparentPillArrowPrimary = 'TransparentPillArrowPrimary',
  TransparentPillArrowLight = 'TransparentPillArrowLight',
  WhitePill = 'WhitePill',
  OffWhitePill = 'OffWhitePill',
  OffWhiteCircleArrow = 'OffWhiteCircleArrow',
  OffWhitePillPrimaryBlueOutline = 'OffWhitePillPrimaryBlueOutline',
  TransparentPillPrimaryBlueOutline = 'TransparentPillPrimaryBlueOutline',
  TransparentPillWhiteOutline = 'TransparentPillWhiteOutline',
  TransparentPrimaryBlueText = 'TransparentPrimaryBlueText',
  TransparentWhiteText = 'TransparentWhiteText',
  ArrowOnlyOffWhite = 'ArrowOnlyOffWhite',
  Arrow = 'Arrow',
}

/**
 * @deprecated Used the Link type for simple label+url navigation and CTA for cases with additional fields
 */
export interface ButtonContent extends Contentful {
  fields: {
    label?: string;
    smallMobileLabel?: string;
    url?: string;
    disabledText?: string;
    variant?: ButtonVariant;
    videoUrl?: string;
  };
}

const pillVariants = [
  ButtonVariant.PrimaryBluePill,
  ButtonVariant.TransparentPillArrowPrimary,
  ButtonVariant.TransparentPillArrowLight,
  ButtonVariant.WhitePill,
  ButtonVariant.OffWhitePill,
  ButtonVariant.OffWhitePillPrimaryBlueOutline,
  ButtonVariant.TransparentPillPrimaryBlueOutline,
  ButtonVariant.TransparentPillWhiteOutline,
  ButtonVariant.ArrowOnlyOffWhite,
  ButtonVariant.Arrow,
  ButtonVariant.TransparentPrimaryBlueText,
  ButtonVariant.TransparentWhiteText,
];

interface Props {
  // Content controlled props
  label?: string;
  url?: string;
  variant?: ButtonVariant;

  // React controlled props
  onClick?:
    | ((event?: React.FormEvent<HTMLButtonElement>) => void | Promise<void>)
    | undefined;
  disabled?: boolean;
  gaEvent?: Event;
  width?: string;
  disableInverseBehavior?: boolean;
}

/**
 * @deprecated use /BaseLibraryButton/Button.tsx instead
 */
export default function OldButton({
  onClick,
  label,
  disabled,
  url = '',
  variant = ButtonVariant.PrimaryBluePill,
  disableInverseBehavior,
  gaEvent,
  width,
}: Props) {
  const { classes } = useStyles();
  const router = useRouter();

  const handleClick = (
    event?:
      | React.FormEvent<HTMLButtonElement>
      | React.FormEvent<HTMLParagraphElement>
  ) => {
    if (event) {
      event.preventDefault();
    }
    if (gaEvent) {
      trackEvents(gaEvent);
    }
    // If onClick is supplied then it takes precedence over content url
    onClick ? onClick() : router.push(url);
  };

  const disableInverseBehaviorStyle = !disableInverseBehavior
    ? { cursor: 'pointer' }
    : { cursor: 'default' };

  // if the onClick param is not defined, then it acts as a link instead of a button
  const linkTarget = openLinkCurrentTab(url) ? '' : '_blank';
  const linkRef = openLinkCurrentTab(url) ? '' : 'noopener noreferrer';

  const transparentArrowColor =
    variant === ButtonVariant.TransparentPillArrowPrimary ? '#202645' : 'white';
  if (onClick === undefined) {
    return (
      <div className={classes.button} style={{ width: width || 'auto' }}>
        {/** biome-ignore lint/correctness/noRestrictedElements: Automated migration to Biome */}
        <a
          className={classNames([
            classes.defaultButton,
            classes[variant],
            disabled && classes.defaultDisabled,
            disabled && classes[`${variant}Disabled`],
          ])}
          href={url}
          target={linkTarget}
          rel={linkRef}
          onClick={() => gaEvent && trackEvents(gaEvent)}
        >
          {(variant === ButtonVariant.OffWhiteCircleArrow ||
            variant === ButtonVariant.ArrowOnlyOffWhite ||
            variant === ButtonVariant.Arrow) && (
            <RightArrow
              fill={variant === ButtonVariant.Arrow ? 'white' : '#202645'}
            />
          )}
          {pillVariants.includes(variant) && label}
          {(variant === ButtonVariant.TransparentPillArrowPrimary ||
            variant === ButtonVariant.TransparentPillArrowLight) && (
            <RightArrow fill={transparentArrowColor} />
          )}
        </a>
        {variant === ButtonVariant.OffWhiteCircleArrow && (
          // biome-ignore lint/correctness/noRestrictedElements: Automated migration to Biome
          <a
            className={classNames([
              classes.offWhiteCircleLabel,
              disabled && classes.offWhiteCircleLabelDisabled,
            ])}
            href={url}
            target={linkTarget}
            rel={linkRef}
            onClick={() => gaEvent && trackEvents(gaEvent)}
          >
            {label}
          </a>
        )}
      </div>
    );
  } else {
    return (
      <div className={classes.button}>
        {/** biome-ignore lint/a11y/useButtonType: Automated migration to Biome */}
        <button
          className={classNames([
            classes.defaultButton,
            classes[variant],
            disabled && !disableInverseBehavior && classes.defaultDisabled,
            disabled &&
              !disableInverseBehavior &&
              classes[`${variant}Disabled`],
          ])}
          onClick={handleClick}
          disabled={disabled}
          style={{ width: width || 'auto', ...disableInverseBehaviorStyle }}
        >
          {(variant === ButtonVariant.OffWhiteCircleArrow ||
            variant === ButtonVariant.ArrowOnlyOffWhite) && <RightArrow />}
          {pillVariants.includes(variant) && label}
          {(variant === ButtonVariant.TransparentPillArrowPrimary ||
            variant === ButtonVariant.TransparentPillArrowLight) && (
            <RightArrow fill={transparentArrowColor} />
          )}
        </button>
        {variant === ButtonVariant.OffWhiteCircleArrow && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: Automated migration to Biome
          <p
            className={classNames([
              classes.offWhiteCircleLabel,
              disabled && classes.offWhiteCircleLabelDisabled,
            ])}
            onClick={handleClick}
          >
            {label}
          </p>
        )}
      </div>
    );
  }
}
